import { ModalConfirmButton } from "@flaminem/ui-components";
import { ErrorTimeout } from "oidc-client-ts";
import { FC, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { AuthContext, AuthContextProps, hasAuthParams, useAuth } from "react-oidc-context";

import { AppUser } from "src/authentication/appUser.ts";
import OverlayLoading from "src/components/DataStates/OverlayLoading.tsx";
import PageError from "src/components/Errors/PageError.tsx";
import { Modal } from "src/components/Modal";
import { GlobalErrorLayout } from "src/errors/GlobalErrorLayout.tsx";
import { HttpClient } from "src/http/httpClient.ts";
import { useT } from "src/internationalization";

let tokenChannel = new BroadcastChannel("token_channel");

export const TokenAuthApp: FC<{ children: ReactNode }> = ({ children }) => {
  const auth: AuthContextProps | null = useAuth();
  const [sessionExpired, setSessionExpired] = useState(false);
  const t = useT();

  const onIdle = async () => {
    setSessionExpired(true);
  };

  useIdleTimer({
    onIdle,
    timeout: 30 * 60_000,
  });

  const handleOnSignin = useCallback(async () => {
    tokenChannel.close();
    tokenChannel = new BroadcastChannel("token_channel");
    console.info("TokenAuthApp: automatically sign-in");
    await auth.signinRedirect({
      redirect_uri: window.location.href,
    });
  }, [auth]);

  // automatically signing-in
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.isLoading &&
      !auth.error &&
      auth.activeNavigator !== "signoutSilent"
    ) {
      void handleOnSignin();
    }
  }, [auth, handleOnSignin]);

  useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading && !auth.error) {
      console.info("[Host] new token issued", auth.user?.access_token.slice(-10));
      // update all classes depending on oidc user
      AppUser.setUser(auth.user ?? null);
      HttpClient.instance.setToken(auth.user?.access_token ?? null);

      // send the token to MFs
      tokenChannel.postMessage({ type: "token", value: auth.user?.access_token ?? "" });

      // send the token if anyone request it explicitly
      tokenChannel.onmessage = (ev) => {
        if (ev.data.type === "request_token") {
          console.info("[Host] send a new token:", auth.user?.access_token.slice(-10) ?? "");
          tokenChannel.postMessage({ type: "token", value: auth.user?.access_token ?? "" });
        }
      };
    }
  }, [auth.error, auth.isAuthenticated, auth.isLoading, auth.user]);

  const handleOnReconnect = async () => {
    setSessionExpired(true);
    await handleOnSignin();
  };

  if (sessionExpired) {
    return (
      <GlobalErrorLayout>
        <Modal
          size="sm"
          title={t("auth.modal.expired.title")}
          buttons={
            <>
              <ModalConfirmButton onClick={handleOnReconnect}>
                {t("auth.modal.expired.button")}
              </ModalConfirmButton>
            </>
          }
        >
          {t("auth.modal.expired.message")}
        </Modal>
      </GlobalErrorLayout>
    );
  }

  // render
  if (
    auth.isAuthenticated ||
    auth.activeNavigator == "signoutSilent" ||
    auth.error instanceof ErrorTimeout
  ) {
    return <div>{children}</div>;
  }
  if (auth.activeNavigator == "signoutRedirect" || auth.isLoading) {
    return <OverlayLoading />;
  }
  if (auth.error) {
    console.error("TokenAuthApp: error", auth.error);
    return (
      <GlobalErrorLayout>
        <PageError
          title={t("auth.error.title")}
          description={
            <>
              <p>{t("auth.error.description")}</p>
              <p>{auth.error.message}</p>
            </>
          }
          onClick={() => (window.location.href = window.location.origin)}
        />
      </GlobalErrorLayout>
    );
  }
  return <GlobalErrorLayout>{t("auth.error.description.default")}</GlobalErrorLayout>;
};

// overrides the default useAuth hook from react-oidc-context to avoid the exception when context is not available
export const useTokenAuth = () => {
  return useContext(AuthContext);
};
